// src/UserContext.tsx
import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import AppConstants from "./AppConstants"; // Adjust path as needed

interface UserProfile {
  firstName: string;
  lastName: string;
  email: string;
  profileImage: string;
}

interface UserContextType {
  user: UserProfile | null;
  isLoading: boolean;
  setUser: (
    user: UserProfile | ((prev: UserProfile | null) => UserProfile)
  ) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) throw new Error("No token");

        const response = await axios.get(AppConstants.ADMIN_PROFILE_API, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.status === 200) {
          setUser({
            firstName: response.data.firstName,
            lastName: response.data.lastName,
            email: response.data.email,
            profileImage:
              response.data.profileImage || "https://placehold.co/150"
          });
        }
      } catch (err: any) {
        if (err?.response?.status === 401) {
          localStorage.clear();
          window.location.href = "/login"; // fallback in case interceptor fails
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  return (
    <UserContext.Provider value={{ user, isLoading, setUser }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) throw new Error("useUser must be used within a UserProvider");
  return context;
};
